import { Box, Stack, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  AlertV2 as Alert,
  Button,
  EAlertV2Type,
  Heading,
  LottieAnimation,
  Text,
} from '@feathr/components';

import { formatCurrency } from '../RedeemCreditsModal.utils';

import * as styles from './RedemptionSuccess.css';

import successAnimation from './assets/success-animation.json';

export interface IRedemptionSuccessProps {
  convertedAmount: number;
  onDone: () => void;
  projectName?: string;
}

function RedemptionSuccess({
  convertedAmount,
  onDone,
  projectName,
}: Readonly<IRedemptionSuccessProps>): JSX.Element {
  const { t } = useTranslation();
  const {
    spacing: { '4': sectionGap },
  } = useMantineTheme();

  return (
    <Stack align={'center'} gap={sectionGap} py={sectionGap}>
      <Box className={styles.animationWrapper}>
        <LottieAnimation animationData={successAnimation} autoplay={true} loop={false} />
      </Box>

      <Heading order={1} variant={'h1'}>
        {t('Success!')}
      </Heading>

      <Text.DefaultRegular>
        {t('You just added ')}
        <Text.DefaultRegular className={styles.amount} inline={true} mantineComponent={'span'}>
          {formatCurrency(convertedAmount)}
        </Text.DefaultRegular>
        {t(' to your marketing budget—for free!')}
      </Text.DefaultRegular>

      <Alert
        title={
          <>
            <Text.DefaultRegular
              className={styles.projectName}
              inline={true}
              mantineComponent={'span'}
            >
              {projectName ?? t('project')}
            </Text.DefaultRegular>
            <span className={styles.alertMessage}>
              {t(' now has increased media balance to fund more campaigns.')}
            </span>
          </>
        }
        type={EAlertV2Type.info}
      />

      <Button isFullWidth={true} onClick={onDone} type={'primary'}>
        {t('Done')}
      </Button>
    </Stack>
  );
}

export default RedemptionSuccess;
